<template>
  <div
    class="user-portal"
    :class="{
      'user-portal--leaderboard': isEarningsLeaderboard,
      'user-portal--assets': activeSection === 'assets',
      'user-portal--bills': activeSection === 'bills',
      'user-portal--profile': activeSection === 'profile'
    }"
  >
    <div v-if="strategyAgreementVisible" class="strategy-agreement-page">
      <HedgingContractTransactionAgreement
        embedded
        @back="closeStrategyAgreement"
      />
    </div>

    <AppHeader
      :active="activeSection"
      :authed="true"
      :exchange="exchange"
      :exchange-options="exchangeOptions"
      :exchange-loading="loading || exchangeSwitching"
      :exchange-error="exchangeListError"
      :search.sync="search"
      :user-name="userName"
      :user-account="userAccount"
      :user-avatar="userAvatar"
      :user-initial="userInitial"
      :user-invitation="userInfo.user_invitation || userInfo.invitation || ''"
      :api-bound-count="apiBoundCount"
      :api-total-count="apiTotalCount"
      :notifications="notificationPreview"
      :notification-count="notificationUnreadCount"
      :notification-loading="notificationPreviewLoading"
      @navigate="navigate"
      @exchange="handleExchangeChange"
      @exchange-retry="retryExchangeList"
      @notification="openNotification"
      @notification-read-all="markAllNotificationPreviewsRead"
      @api-binding="openApiBinding"
      @profile="openPersonalCenter"
      @logout="openLogoutConfirm"
      @language-change="handleLanguageChange"
    />

    <transition name="mobile-account-sheet">
      <div
        v-if="mobileAccountSheetVisible"
        class="mobile-account-sheet"
        role="dialog"
        aria-modal="true"
        @click.self="closeMobileAccountSheet"
      >
        <button
          class="mobile-account-sheet__backdrop"
          type="button"
          :aria-label="$t('关闭')"
          @click="closeMobileAccountSheet"
        />
        <section class="mobile-account-sheet__panel" @click.stop>
          <i class="mobile-account-sheet__handle" aria-hidden="true" />
          <PersonalCenterPanel
            embedded-mobile
            :mobile-view="mobileAccountView"
            :user-info="userInfo"
            :exchange-robots="exchangeRobots"
            :last-sync="lastSync"
            :language="portalLanguage"
            @user-updated="applyUpdatedUser"
            @refresh-exchanges="refreshExchangeRobots"
            @password-changed="handlePasswordChanged"
            @toast="handlePanelToast"
          />
        </section>
      </div>
    </transition>

    <main class="up-container portal-main">
      <transition name="portal-panel" mode="out-in">
        <OrbitStrategyDetailPanel
          v-if="isStrategyDetail && selectedDetailRobot && selectedDetailRobot.productType === 'orbit'"
          :key="`orbit-detail-${selectedDetailRobot.id}`"
          :robot="selectedDetailRobot"
          :robot-info="activeExchangeRobot"
          :all-robot-infos="exchangeRobots"
          @back="closeStrategyDetail"
          @action="openRobotAction"
          @record="applyOrbitRecord"
          @toast="handleDetailToast"
        />
        <StrategyDetailPanel
          v-else-if="isStrategyDetail && selectedDetailRobot"
          :key="`strategy-detail-${selectedDetailRobot.id}`"
          :robot="selectedDetailRobot"
          :robot-info="activeExchangeRobot"
          :all-robot-infos="exchangeRobots"
          @back="closeStrategyDetail"
          @action="openRobotAction"
          @refresh="fetchSnapshot"
          @toast="handleDetailToast"
        />
        <div
          v-else-if="isStrategyDetail"
          key="strategy-detail-empty"
          class="detail-route-state up-card"
        >
          <i :class="loading ? 'el-icon-loading' : 'el-icon-warning-outline'" aria-hidden="true" />
          <strong>{{ loading ? '正在读取策略详情…' : '未找到该策略' }}</strong>
          <p>{{ loading ? '正在定位当前策略' : '策略可能已删除，或不属于当前交易所。' }}</p>
          <button v-if="!loading" type="button" @click="closeStrategyDetail">返回机器人</button>
        </div>
        <NotificationsPanel
          v-else-if="isNotificationsPage"
          :key="`notifications-${portalLanguage}`"
          :message-id="$route.params.messageId || ''"
          @open="openNotification"
          @back="closeNotificationDetail"
          @read="markNotificationPreviewRead"
          @unread-count="setNotificationUnreadCount"
          @toast="handlePanelToast"
        />
        <OverviewPanel
          v-else-if="activeSection === 'overview'"
          key="overview"
          :user-name="userName"
          :robots="filteredRobots"
          :balances="balances"
          :earnings="earnings"
          :period="earningsPeriod"
          :period-loading="earningsPeriodLoading"
          :registration-time="userInfo.create_time || userInfo.register_time || userInfo.add_time || ''"
          :leaders="leaders"
          :robot-risk="robotRisk"
          :rate="rate"
          :last-sync="lastSync"
          :loading="loading"
          :errors="loadErrors"
          @navigate="navigate"
          @robot="openRobot"
          @action="openAction"
          @support="openSupport"
          @leaderboard="openEarningsLeaderboard"
          @period-change="changeEarningsPeriod"
        />
        <RobotsPanel
          v-else-if="activeSection === 'robots'"
          key="robots"
          :robots="filteredRobots"
          :earnings="earnings"
          :loading="loading"
          :exchange="exchange"
          :robot-type="selectedRobotType"
          :errors="loadErrors"
          :robot-info="activeExchangeRobot"
          :all-robot-infos="exchangeRobots"
          :power-loading="robotPowerSubmitting"
          :strategy-mode="strategyListMode"
          :strategy-list-loading="strategyListLoading"
          :history-page="historyStrategyPagination.page"
          :history-page-size="historyStrategyPagination.pageSize"
          :history-total="historyStrategyPagination.total"
          @strategy-mode-change="handleStrategyModeChange"
          @history-page-change="handleHistoryStrategyPageChange"
          @refresh="fetchSnapshot"
          @robot="openRobot"
          @robot-menu="openRobotAction"
          @robot-exception="openRobotException"
          @robot-power="handleRobotPower"
          @create="openStrategyCreate"
        />
        <EarningsLeaderboardPanel
          v-else-if="isEarningsLeaderboard"
          key="earnings-leaderboard"
          :leaders="leaderboardLeaders"
          :rank="leaderboardRank"
          :user-info="userInfo"
          :period="leaderboardPeriod"
          :loading="leaderboardLoading"
          @back="closeEarningsLeaderboard"
          @navigate="navigate"
          @period-change="changeLeaderboardPeriod"
        />
        <EarningsPanel
          v-else-if="activeSection === 'earnings'"
          key="earnings"
          :robots="filteredRobots"
          :earnings="earnings"
          :leaders="leaders"
          :period="earningsPeriod"
          :loading="loading || earningsDetailsLoading"
          @leaderboard="openEarningsLeaderboard"
          @period-change="changeEarningsPeriod"
        />
        <AssetsPanel
          v-else-if="activeSection === 'assets'"
          :key="`assets-${portalLanguage}`"
          :balances="balances"
          :records="recordRows"
          :user-info="userInfo"
          :earnings="earnings"
          :rate="rate"
          :loading="recordLoading"
          :refreshing="assetRefreshing"
          :last-sync="lastSync"
          :order-pagination="assetOrderPagination"
          :hide-fuel-account="hideFuelAccount"
          @action="openAction"
          @navigate="navigate"
          @order-page="changeAssetOrderPage"
          @refresh="refreshAssetSummary"
          @refresh-records="refreshAssetOrderRecords"
        />
        <PersonalCenterPanel
          v-else-if="activeSection === 'profile'"
          ref="personalCenterPanel"
          key="profile"
          :user-info="userInfo"
          :exchange-robots="exchangeRobots"
          :last-sync="lastSync"
          :language="portalLanguage"
          @user-updated="applyUpdatedUser"
          @refresh-exchanges="refreshExchangeRobots"
          @password-changed="handlePasswordChanged"
          @toast="handlePanelToast"
        />
        <BillsPanel
          v-else
          key="bills"
          :rows="billFlowRows"
          :bill-rows="billRecordRows"
          :trades="tradeRows"
          :balances="balances"
          :earnings="earnings"
          :period="billRecordPeriod"
          :loading="billFlowLoading"
          :record-loading="billRecordLoading"
          :record-pagination="billRecordPagination"
          :record-summary="billRecordSummary"
          :flow-pagination="billOrderPagination"
          :account-records="billAccountRecords"
          :account-loading="billAccountLoading"
          :account-pagination="billAccountPagination"
          :period-loading="earningsPeriodLoading"
          :initial-category="$route.query.category"
          @record-page="changeBillRecordPage"
          @record-period-change="changeBillRecordPeriod"
          @tab-change="handleBillTabChange"
          @flow-category-change="changeBillFlowCategory"
          @flow-page="changeBillFlowPage"
          @account-category-change="changeBillAccountCategory"
          @account-page="changeBillAccountPage"
          @period-change="changeEarningsPeriod"
        />
      </transition>
    </main>

    <AppFooter :authed="true" />

    <WithdrawalTypeModal
      :visible.sync="withdrawalTypeVisible"
      @select="openWithdrawalAction"
    />
    <ActionModal
      :visible.sync="actionVisible"
      :type="actionType"
      :submitting="submitting"
      :point-cards="pointCards"
      :point-cards-loading="pointCardsLoading"
      :user-account="userInfo.username || userInfo.mobile || ''"
      :user-email="userInfo.username || ''"
      :user-mobile="userInfo.mobile || ''"
      :balances="balances"
      :initial-transfer-from="actionTransferFrom"
      @submit="handleActionSubmit"
      @send-code="handleSendCode"
    />
    <DepositModal
      :visible.sync="depositVisible"
      :methods="depositMethods"
      :methods-loading="depositMethodsLoading"
      :submitting="depositSubmitting"
      :order="depositOrder"
      :qr-code-url="depositQrCodeUrl"
      @submit="handleDepositSubmit"
      @reload-methods="reloadDepositMethods"
      @closed="resetDepositFlow"
    />
    <StrategyRiskConfirmModal
      :visible.sync="strategyRiskVisible"
      :product="strategyRiskProduct"
      @confirm="confirmStrategyRisk"
      @agreement="openStrategyAgreement"
      @closed="handleStrategyRiskClosed"
    />
    <StrategyCreateModal
      :visible.sync="strategyCreateVisible"
      :initial-product-key="strategyProductKey"
      :initial-capacity="strategyCapacity"
      :exchange-robots="exchangeRobots"
      :current-exchange="exchange"
      :robot-info="activeExchangeRobot"
      :submitting="strategySubmitting"
      @submit="handleStrategyCreate"
    />
    <OrbitStrategyCreateModal
      :visible.sync="orbitStrategyCreateVisible"
      :exchange="exchange"
      :robot-info="activeExchangeRobot"
      :submitting="strategySubmitting"
      @submit="handleOrbitStrategyCreate"
    />
    <OrbitStrategyActionModal
      :visible.sync="orbitActionVisible"
      :action="orbitActionType"
      :robot="orbitActionRobot"
      :submitting="orbitActionSubmitting"
      @submit="handleOrbitActionSubmit"
    />
    <RobotStrategyActionModal
      :visible.sync="robotActionVisible"
      :action="robotActionType"
      :robot="robotActionRobot"
      :submitting="robotActionSubmitting"
      @submit="handleRobotActionSubmit"
    />
    <RobotStrategyExceptionModal
      :visible.sync="robotExceptionVisible"
      :phase="robotExceptionPhase"
      :robot="robotExceptionRobot"
      :submitting="robotExceptionSubmitting"
      @modify="openRobotExceptionModify"
      @submit="handleRobotExceptionSubmit"
    />
    <RobotPowerConfirmModal
      :visible.sync="robotPowerConfirmVisible"
      :message="robotPowerConfirmMessage"
      :prompt="robotPowerConfirmPrompt"
      :submitting="robotPowerSubmitting"
      @confirm="confirmRobotPowerClose"
    />
    <LogoutConfirmModal
      :visible.sync="logoutConfirmVisible"
      @confirm="confirmLogout"
    />
    <ToastMessage :visible="toast.visible" :message="toast.message" :type="toast.type" :style="orbitStrategyCreateVisible || orbitActionVisible ? { zIndex: 14010 } : null" />
  </div>
</template>

<script>
import AppFooter from '@/components/rebuild/AppFooter.vue'
import AppHeader from '@/components/rebuild/AppHeader.vue'
import ActionModal from '@/components/rebuild/ActionModal.vue'
import WithdrawalTypeModal from '@/components/rebuild/WithdrawalTypeModal.vue'
import DepositModal from '@/components/rebuild/DepositModal.vue'
import HedgingContractTransactionAgreement from '@/components/HedgingContractTransactionAgreement.vue'
import LogoutConfirmModal from '@/components/rebuild/LogoutConfirmModal.vue'
import RobotPowerConfirmModal from '@/components/rebuild/RobotPowerConfirmModal.vue'
import RobotStrategyActionModal from '@/components/rebuild/RobotStrategyActionModal.vue'
import RobotStrategyExceptionModal from '@/components/rebuild/RobotStrategyExceptionModal.vue'
import OrbitStrategyCreateModal from '@/components/rebuild/OrbitStrategyCreateModal.vue'
import OrbitStrategyActionModal from '@/components/rebuild/OrbitStrategyActionModal.vue'
import OrbitStrategyDetailPanel from './panels/OrbitStrategyDetailPanel.vue'
import orbitStrategy from '@/mixins/orbit-strategy'
import StrategyCreateModal from '@/components/rebuild/StrategyCreateModal.vue'
import StrategyRiskConfirmModal from '@/components/rebuild/StrategyRiskConfirmModal.vue'
import ToastMessage from '@/components/rebuild/ToastMessage.vue'
import OverviewPanel from './panels/OverviewPanel.vue'
import RobotsPanel from './panels/RobotsPanel.vue'
import StrategyDetailPanel from './panels/StrategyDetailPanel.vue'
import EarningsPanel from './panels/EarningsPanel.vue'
import EarningsLeaderboardPanel from './panels/EarningsLeaderboardPanel.vue'
import AssetsPanel from './panels/AssetsPanel.vue'
import BillsPanel from './panels/BillsPanel.vue'
import PersonalCenterPanel from './panels/PersonalCenterPanel.vue'
import NotificationsPanel from './panels/NotificationsPanel.vue'
import { readLanguage } from '@/utils/locale'
import binanceLogo from '@/assets/exchanges/binance.png'
import okxLogo from '@/assets/exchanges/okx.png'
import gateLogo from '@/assets/exchanges/gate.png'
import bybitLogo from '@/assets/exchanges/bybit.png'
import {
  loadNotificationPage,
  loadNotificationUnreadCount,
  markAllNotificationsRead
} from '@/api/notifications'
import {
  buyPointCard,
  createRechargeOrder,
  loadAccountBalances,
  loadAssetOrderRecords,
  loadCardTransferRecords,
  loadAssetSummary,
  loadAssetRecords,
  loadDepositRecords,
  internalWithdrawBalance,
  loadEarningsDetails,
  loadEarningsLeaderboard,
  loadEarningsMonth,
  loadExchangeHistoryStrategies,
  loadExchangeStrategies,
  loadPortalSnapshot,
  loadStrategyCapacity,
  loadPointCardOptions,
  loadRechargeMethods,
  loadRechargeOrderQrCode,
  loadRobotRisk,
  sendWithdrawCode,
  transferBalance,
  withdrawBalance
} from '@/api/portal'
import {
  createStrategy,
  getStrategyProduct,
  strategyCapacityAfterCreate,
  strategyAvailabilityIssue,
  strategySpaceAvailabilityIssue,
  strategyCreateSucceeded
} from '@/api/strategy'
import {
  executeRobotAction,
  loadRobotActionDetail,
  robotNeedsMaximumRestart,
  robotProductType,
  robotTypeValue
} from '@/api/robot-actions'
import {
  executeRobotPower,
  loadRobotPowerState
} from '@/api/robot-power'
import { getRobotList, saveRobotName } from '@/server/robot'
import { createOrbitStrategy } from '@/api/orbit-strategy'
import {
  finiteNumber,
  firstNumber,
  mapAssetRecords,
  mapLeader,
  mapRobot,
  maskAccount
} from '@/utils/portal-data'

const allowedSections = ['overview', 'robots', 'earnings', 'assets', 'bills', 'profile']
const EXCHANGE_NAMES = {
  0: 'Binance',
  1: 'OKX',
  2: 'Gate',
  3: 'Bybit'
}
const EXCHANGE_LOGOS = {
  0: binanceLogo,
  1: okxLogo,
  2: gateLogo,
  3: bybitLogo
}

function readCachedUserInfo() {
  try {
    const cached = window.localStorage.getItem('userInfo')
    const parsed = cached ? JSON.parse(cached) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch (error) {
    return {}
  }
}

function readRememberedAccount() {
  try {
    return String(window.localStorage.getItem('uplusRememberedAccount') || '').trim()
  } catch (error) {
    return ''
  }
}

function exchangeName(robotType) {
  if (robotType === null || robotType === undefined) return '暂无交易所'
  return EXCHANGE_NAMES[Number(robotType)] || `交易所 ${robotType}`
}

function emptyRecords() {
  return {
    recharges: [],
    withdrawals: [],
    rechargeTotal: 0,
    withdrawalTotal: 0,
    depositTotal: 0,
    changes: [],
    deposits: [],
    pointCards: [],
    errors: []
  }
}

export default {
  name: 'UserPortal',
  mixins: [orbitStrategy],
  components: {
    AppHeader,
    AppFooter,
    ActionModal,
    WithdrawalTypeModal,
    DepositModal,
    HedgingContractTransactionAgreement,
    LogoutConfirmModal,
    RobotPowerConfirmModal,
    RobotStrategyActionModal,
    RobotStrategyExceptionModal,
    OrbitStrategyCreateModal,
    OrbitStrategyActionModal,
    OrbitStrategyDetailPanel,
    StrategyCreateModal,
    StrategyRiskConfirmModal,
    ToastMessage,
    OverviewPanel,
    RobotsPanel,
    StrategyDetailPanel,
    EarningsPanel,
    EarningsLeaderboardPanel,
    AssetsPanel,
    BillsPanel,
    PersonalCenterPanel,
    NotificationsPanel
  },
  data() {
    const now = new Date()
    return {
      portalLanguage: readLanguage(),
      exchange: '交易所加载中',
      selectedRobotType: null,
      strategyListMode: 'current',
      strategyListLoading: false,
      strategyListRequestId: 0,
      historyStrategyPagination: { page: 1, pageSize: 10, total: 0 },
      exchangeSwitching: false,
      exchangeListError: '',
      snapshotRequestId: 0,
      search: '',
      notificationPreview: [],
      notificationUnreadCount: 0,
      notificationPreviewLoading: false,
      notificationPreviewRequestId: 0,
      notificationRefreshPending: false,
      notificationTimer: null,
      userInfo: readCachedUserInfo(),
      rememberedAccount: readRememberedAccount(),
      loading: true,
      recordLoading: false,
      loadErrors: [],
      lastSync: null,
      actionVisible: false,
      withdrawalTypeVisible: false,
      actionType: 'transfer',
      actionTransferFrom: '1',
      submitting: false,
      depositVisible: false,
      depositMethods: [],
      depositMethodsLoading: false,
      depositSubmitting: false,
      depositOrder: null,
      depositQrCodeUrl: '',
      strategySubmitting: false,
      strategyCreateVisible: false,
      orbitStrategyCreateVisible: false,
      strategyRiskVisible: false,
      strategyRiskApproved: false,
      strategyAgreementVisible: false,
      strategyProductKey: 'astra',
      strategyCapacity: null,
      robotActionVisible: false,
      robotActionType: 'modify',
      robotActionRobot: { raw: {} },
      robotActionSubmitting: false,
      robotExceptionVisible: false,
      robotExceptionPhase: 'warning',
      robotExceptionRobot: { raw: {} },
      robotExceptionSubmitting: false,
      robotPowerSubmitting: false,
      robotPowerConfirmVisible: false,
      robotPowerPending: null,
      logoutConfirmVisible: false,
      mobileAccountSheetVisible: false,
      mobileAccountView: 'api',
      detailRobotCache: null,
      assetRefreshing: false,
      assetRecords: emptyRecords(),
      assetRecordsLoaded: false,
      assetOrderPagination: {
        deposit: { page: 1, pageSize: 10, total: 0 },
        withdraw: { page: 1, pageSize: 10, total: 0 }
      },
      billRecords: [],
      billRecordLoaded: false,
      billRecordLoading: false,
      billRecordPendingPeriod: null,
      billRecordPeriod: { year: now.getFullYear(), month: now.getMonth() + 1 },
      billRecordPagination: { page: 1, pageSize: 10, total: 0 },
      billRecordSummary: { allDeposit: null },
      billFlowRecords: emptyRecords(),
      billFlowLoading: false,
      billOrderPagination: {
        deposit: { page: 1, pageSize: 10, total: 0 },
        withdraw: { page: 1, pageSize: 10, total: 0 }
      },
      billFlowLoaded: {
        deposit: false,
        withdraw: false
      },
      billAccountRecords: {
        pointCard: [],
        fuelTransfer: []
      },
      billPointCardSource: [],
      billAccountLoading: false,
      billAccountPagination: {
        pointCard: { page: 1, pageSize: 10, total: 0 },
        fuelTransfer: { page: 1, pageSize: 10, total: 0 }
      },
      billAccountLoaded: {
        pointCard: false,
        fuelTransfer: false
      },
      pointCards: [],
      pointCardsLoading: false,
      exchangeRobots: [],
      rate: null,
      leaders: [],
      leaderboardPeriod: 'week',
      leaderboardLeaders: [],
      leaderboardRank: null,
      leaderboardLoading: false,
      leaderboardErrors: [],
      leaderboardRequestId: 0,
      robotRisk: null,
      robotRiskRobotType: null,
      robotRiskLoading: false,
      robots: [],
      earningsPeriod: { year: now.getFullYear(), month: now.getMonth() + 1 },
      earningsPeriodLoading: false,
      earningsPeriodRequestId: 0,
      earningsDetailsLoading: false,
      earningsDetailsLoadedRobotType: null,
      earningsDetailsRequestId: 0,
      earnings: {
        profit: null,
        yield: null,
        avgYield: null,
        todayProfit: null,
        todayYield: null,
        monthProfit: null,
        monthYield: null,
        monthRows: [],
        allRows: [],
        profitLine: [],
        rank: null,
        distribution: {},
        records: []
      },
      balances: {
        total: null,
        fund: null,
        fuel: null,
        card: null
      },
      toast: { visible: false, message: '', type: 'info', timer: null }
    }
  },
  computed: {
    activeSection() {
      if (this.$route.name === 'strategy-detail') return 'robots'
      if (this.$route.name === 'earnings-leaderboard') return 'earnings'
      if (this.isNotificationsPage) return ''
      const section = this.$route.params.section || 'overview'
      return allowedSections.includes(section) ? section : 'overview'
    },
    strategyRiskProduct() {
      return getStrategyProduct(this.strategyProductKey)
    },
    isStrategyDetail() {
      return this.$route.name === 'strategy-detail'
    },
    isEarningsLeaderboard() {
      return this.$route.name === 'earnings-leaderboard'
    },
    isNotificationsPage() {
      return ['notifications', 'notification-detail'].includes(this.$route.name)
    },
    selectedDetailRobot() {
      if (!this.isStrategyDetail) return null
      const strategyId = String(this.$route.params.strategyId || '')
      const routeRobotType = this.$route.query.robotType
      const current = this.robots.find(robot =>
        String(robot.id) === strategyId &&
        (
          routeRobotType === undefined ||
          Number(robotTypeValue(robot)) === Number(routeRobotType)
        )
      )
      if (current) return current
      if (
        this.detailRobotCache &&
        String(this.detailRobotCache.id) === strategyId &&
        (
          routeRobotType === undefined ||
          Number(robotTypeValue(this.detailRobotCache)) === Number(routeRobotType)
        )
      ) {
        return this.detailRobotCache
      }
      return null
    },
    userName() {
      const source =
        this.userInfo.name ||
        this.userInfo.nickname ||
        this.userInfo.username ||
        this.userInfo.email ||
        this.userInfo.mobile ||
        this.rememberedAccount
      return source ? maskAccount(source) : 'UPlus'
    },
    userAccount() {
      return String(
        this.userInfo.username ||
        this.userInfo.email ||
        this.userInfo.mobile ||
        this.rememberedAccount ||
        ''
      )
    },
    userAvatar() {
      return String(
        this.userInfo.user_face_head ||
        this.userInfo.user_face ||
        this.userInfo.avatar ||
        this.userInfo.head_img ||
        ''
      )
    },
    userInitial() {
      const source = String(
        this.userInfo.name ||
        this.userInfo.nickname ||
        this.userInfo.username ||
        this.userInfo.email ||
        this.userInfo.mobile ||
        this.rememberedAccount ||
        'U'
      )
      const local = source.includes('@') ? source.split('@')[0] : source
      const match = local.trim().match(/[A-Za-z0-9\u3400-\u9fff]/)
      return (match ? match[0] : 'U').toUpperCase()
    },
    hideFuelAccount() {
      return Number(this.userInfo && this.userInfo.is_his) !== 0
    },
    apiTotalCount() {
      return this.exchangeRobots.length
    },
    apiBoundCount() {
      return this.exchangeRobots.filter(item => Number(item && item.bind_api) === 1).length
    },
    exchangeOptions() {
      const seen = new Set()
      return this.exchangeRobots.reduce((options, item) => {
        const robotType = Number(item && item.robot_type)
        if (!Number.isFinite(robotType) || seen.has(robotType)) return options
        seen.add(robotType)
        options.push({
          command: String(robotType),
          label: exchangeName(robotType),
          logo: EXCHANGE_LOGOS[robotType] || '',
          bound: Number(item && item.bind_api) === 1
        })
        return options
      }, [])
    },
    activeExchangeRobot() {
      if (this.selectedRobotType === null) return null
      return this.exchangeRobots.find(item =>
        Number(item && item.robot_type) === this.selectedRobotType
      ) || null
    },
    robotPowerConfirmMessage() {
      const pendingRobot = this.robotPowerPending &&
        this.robotPowerPending.robots &&
        this.robotPowerPending.robots[0]
      const exchangeLabel = pendingRobot && pendingRobot.exchange
        ? pendingRobot.exchange
        : this.exchange
      return this.$t(`关闭 ${exchangeLabel} 机器人后，将停止对该交易所策略的监控及运行`)
    },
    robotPowerConfirmPrompt() {
      const pendingRobot = this.robotPowerPending &&
        this.robotPowerPending.robots &&
        this.robotPowerPending.robots[0]
      const exchangeLabel = pendingRobot && pendingRobot.exchange
        ? pendingRobot.exchange
        : this.exchange
      return this.$t(`确定要关闭 ${exchangeLabel} 机器人吗？`)
    },
    filteredRobots() {
      const keyword = this.search.trim().toLowerCase()
      const exchange = this.selectedRobotType === null ? '' : this.exchange.toLowerCase()
      return this.robots.filter(robot => {
        const matchesKeyword = !keyword ||
          `${robot.name} ${robot.symbol} ${robot.exchange}`.toLowerCase().includes(keyword)
        const matchesExchange = !exchange || robot.exchange.toLowerCase() === exchange
        return matchesKeyword && matchesExchange
      })
    },
    recordRows() {
      return mapAssetRecords(this.assetRecords)
    },
    billFlowRows() {
      return mapAssetRecords(this.billFlowRecords)
    },
    billRecordRows() {
      return mapAssetRecords({ deposits: this.billRecords })
    },
    tradeRows() {
      return this.recordRows.filter(row => row.category === 'profit')
    }
  },
  watch: {
    logoutConfirmVisible(visible, previous) {
      if (previous && !visible) this.$root.$emit('mobile-account-child-closed')
    },
    isEarningsLeaderboard: {
      immediate: true,
      handler(active) {
        if (active) this.loadLeaderboard(this.leaderboardPeriod)
      }
    },
    activeSection: {
      immediate: true,
      handler(section) {
        if (section === 'profile' && !this.loading) {
          this.refreshExchangeRobots()
        }
        if (section === 'assets' && !this.recordLoading && !this.assetRecordsLoaded) {
          this.fetchAssetRecords()
        }
        if (section === 'bills') {
          const category = String(this.$route.query.category || '')
          if (!this.billRecordLoading && !this.billRecordLoaded) {
            this.fetchBillRecordPage(1)
          }
          if (['deposit', 'withdraw'].includes(category)) {
            this.ensureBillFlowRecords(category)
          }
        }
        if (
          section === 'earnings' &&
          this.isMobileViewport() &&
          !this.isEarningsLeaderboard &&
          this.selectedRobotType !== null
        ) {
          this.loadEarningsDetailsForExchange()
        }
        if (
          section === 'overview' &&
          this.selectedRobotType !== null &&
          Number(this.robotRiskRobotType) !== Number(this.selectedRobotType)
        ) {
          this.loadOverviewRobotRisk()
        }
      }
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.replace({ path: '/login', query: { redirect: this.$route.fullPath } })
      return
    }
    try {
      const cached = sessionStorage.getItem('uplusStrategyDetail')
      this.detailRobotCache = cached ? JSON.parse(cached) : null
    } catch (error) {
      sessionStorage.removeItem('uplusStrategyDetail')
      this.detailRobotCache = null
    }
    this.initializePortal()
    this.startNotificationPolling()
    document.addEventListener('visibilitychange', this.handleNotificationVisibility)
  },
  beforeDestroy() {
    this.stopNotificationPolling()
    document.removeEventListener('visibilitychange', this.handleNotificationVisibility)
    this.releaseDepositQrCode()
  },
  methods: {
    handleLanguageChange(language) {
      this.portalLanguage = language
      this.refreshNotificationSummary()
    },
    async refreshNotificationSummary() {
      if (this.notificationRefreshPending) return
      const requestId = ++this.notificationPreviewRequestId
      const showInitialLoading = this.notificationPreview.length === 0
      this.notificationRefreshPending = true
      if (showInitialLoading) this.notificationPreviewLoading = true
      try {
        const [announcements, unreadCount] = await Promise.all([
          loadNotificationPage(7, 1, 4),
          loadNotificationUnreadCount(0)
        ])
        if (requestId !== this.notificationPreviewRequestId) return
        this.notificationPreview = announcements.rows.slice(0, 4)
        this.notificationUnreadCount = unreadCount
      } catch (error) {
        // 保留上一次成功数据，等待下一次定时刷新。
      } finally {
        if (requestId === this.notificationPreviewRequestId) {
          this.notificationRefreshPending = false
          this.notificationPreviewLoading = false
        }
      }
    },
    startNotificationPolling() {
      this.stopNotificationPolling()
      this.refreshNotificationSummary()
      this.notificationTimer = window.setInterval(() => {
        this.refreshNotificationSummary()
      }, 30000)
    },
    stopNotificationPolling() {
      if (!this.notificationTimer) return
      window.clearInterval(this.notificationTimer)
      this.notificationTimer = null
    },
    handleNotificationVisibility() {
      if (document.hidden) {
        this.stopNotificationPolling()
        return
      }
      this.startNotificationPolling()
    },
    openNotification(item = {}) {
      const id = String(item.id || '').trim()
      if (!id) return
      const query = Number(item.type) > 0 ? { type: String(item.type) } : {}
      const target = { path: `/app/notifications/${encodeURIComponent(id)}`, query }
      if (this.$route.fullPath !== this.$router.resolve(target).route.fullPath) {
        this.$router.push(target)
      }
    },
    closeNotificationDetail() {
      const type = Number(this.$route.query.type)
      const query = [1, 2, 6, 7].includes(type) ? { type: String(type) } : {}
      this.$router.push({ path: '/app/notifications', query })
    },
    markNotificationPreviewRead(id) {
      const messageId = String(id || '')
      const current = this.notificationPreview.find(item => String(item.id) === messageId)
      this.notificationPreview = this.notificationPreview.map(item =>
        String(item.id) === messageId ? { ...item, state: 2, unread: false } : item
      )
      if (current && current.unread) {
        this.notificationUnreadCount = Math.max(this.notificationUnreadCount - 1, 0)
      }
    },
    setNotificationUnreadCount(count) {
      const value = Number(count)
      this.notificationUnreadCount = Number.isFinite(value) && value > 0 ? value : 0
    },
    async markAllNotificationPreviewsRead() {
      try {
        await markAllNotificationsRead()
        this.notificationPreview = this.notificationPreview.map(item => ({ ...item, state: 2, unread: false }))
        this.notificationUnreadCount = 0
        this.showToast(this.$t('全部通知已标记为已读'), 'success')
      } catch (error) {
        this.showToast(error.message || this.$t('全部已读操作失败'), 'error')
      }
    },
    async initializePortal() {
      const snapshot = await this.fetchSnapshot()
      if (
        snapshot &&
        this.activeSection === 'earnings' &&
        this.isMobileViewport() &&
        !this.isEarningsLeaderboard &&
        this.selectedRobotType !== null
      ) {
        await this.loadEarningsDetailsForExchange()
      }
      if (!snapshot || this.selectedRobotType === null) return
      try {
        const robotInfo = await loadRobotPowerState(this.selectedRobotType)
        this.applyRobotInfo(this.selectedRobotType, robotInfo)
      } catch (error) {
        this.showToast(error.message || this.$t('机器人状态读取失败'), 'error')
      }
      if (this.activeSection === 'overview') {
        await this.loadOverviewRobotRisk(this.selectedRobotType)
      }
    },
    navigate(section, query = {}) {
      if (!allowedSections.includes(section)) return
      const path = `/app/${section}`
      const targetQuery = query && typeof query === 'object' ? query : {}
      const currentQuery = JSON.stringify(this.$route.query || {})
      const nextQuery = JSON.stringify(targetQuery)
      if (this.$route.path !== path || currentQuery !== nextQuery) {
        this.$router.push({ path, query: targetQuery })
      }
    },
    openApiBinding() {
      if (this.isMobileViewport()) {
        this.openMobileAccountSheet('api')
        return
      }
      const target = { path: '/app/profile', query: { focus: 'api' } }
      const scrollToApi = () => {
        this.$nextTick(() => {
          const section = document.getElementById('profile-api-section')
          if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      }
      if (this.$route.path === target.path) {
        if (this.$route.query.focus === 'api') scrollToApi()
        else this.$router.push(target).then(scrollToApi)
        return
      }
      this.$router.push(target).then(scrollToApi)
    },
    openPersonalCenter(action = '') {
      if (this.isMobileViewport() && ['personal-info', 'password'].includes(action)) {
        this.openMobileAccountSheet(action)
        return
      }
      const openRequestedPanel = () => {
        this.$nextTick(() => {
          const panel = this.$refs.personalCenterPanel
          if (!panel) return
          if (action === 'personal-info' && typeof panel.openProfile === 'function') panel.openProfile()
          if (action === 'password' && typeof panel.openPassword === 'function') panel.openPassword()
        })
      }
      const target = { path: '/app/profile' }
      if (this.$route.path === target.path) {
        openRequestedPanel()
        return
      }
      this.$router.push(target).then(openRequestedPanel)
    },
    isMobileViewport() {
      return typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
    },
    openMobileAccountSheet(view) {
      this.mobileAccountView = view
      this.mobileAccountSheetVisible = true
    },
    closeMobileAccountSheet() {
      this.mobileAccountSheetVisible = false
      this.$root.$emit('mobile-account-child-closed')
    },
    applyUpdatedUser(user) {
      if (!user || typeof user !== 'object') return
      this.userInfo = { ...this.userInfo, ...user }
      localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
    },
    handlePanelToast(payload = {}) {
      this.showToast(payload.message || '', payload.type || 'info')
    },
    async refreshExchangeRobots() {
      try {
        const result = await getRobotList()
        if (!result || result.success === false) {
          throw new Error((result && result.message) || this.$t('交易所状态读取失败'))
        }
        this.exchangeRobots = Array.isArray(result.data) ? result.data : []
        const current = this.exchangeRobots.find(item =>
          Number(item && item.robot_type) === Number(this.selectedRobotType)
        )
        if (current) this.applyRobotInfo(this.selectedRobotType, current)
      } catch (error) {
        this.showToast(error.message || this.$t('交易所状态读取失败'), 'error')
      }
    },
    async retryExchangeList() {
      if (this.exchangeSwitching) return
      this.exchangeSwitching = true
      this.exchangeListError = ''
      try {
        const result = await getRobotList()
        if (!result || result.success === false) {
          throw new Error((result && result.message) || this.$t('交易所列表读取失败'))
        }
        const data = result.data
        const list = Array.isArray(data)
          ? data
          : (data && Array.isArray(data.list) ? data.list : [])
        this.exchangeRobots = list
        if (!list.length) {
          this.selectedRobotType = null
          this.exchange = '暂无可用交易所'
          return
        }

        const currentExists = list.some(item =>
          Number(item && item.robot_type) === Number(this.selectedRobotType)
        )
        if (currentExists) {
          this.exchange = exchangeName(this.selectedRobotType)
          return
        }

        const firstRobotType = Number(list[0] && list[0].robot_type)
        if (!Number.isFinite(firstRobotType)) throw new Error(this.$t('交易所列表数据异常'))
        this.exchangeSwitching = false
        await this.handleExchangeChange(String(firstRobotType))
      } catch (error) {
        this.exchangeListError = error.message || this.$t('交易所列表读取失败')
        this.exchange = '交易所加载失败'
        this.showToast(this.exchangeListError, 'error')
      } finally {
        this.exchangeSwitching = false
      }
    },
    handlePasswordChanged() {
      setTimeout(() => {
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        sessionStorage.removeItem('uplusStrategyDetail')
        this.$router.replace('/login')
      }, 900)
    },
    openEarningsLeaderboard() {
      if (!this.isEarningsLeaderboard) this.$router.push('/app/earnings/leaderboard')
    },
    closeEarningsLeaderboard() {
      if (this.$route.path !== '/app/earnings') this.$router.push('/app/earnings')
    },
    async changeLeaderboardPeriod(period) {
      if (!['week', 'month', 'all'].includes(period)) return
      if (period === this.leaderboardPeriod && this.leaderboardLeaders.length) return
      this.leaderboardPeriod = period
      await this.loadLeaderboard(period)
    },
    async loadLeaderboard(period = this.leaderboardPeriod) {
      const dateTypes = { week: 1, month: 2, all: 0 }
      const dateType = dateTypes[period]
      if (dateType === undefined) return
      const requestId = ++this.leaderboardRequestId
      this.leaderboardLoading = true
      this.leaderboardErrors = []
      try {
        const result = await loadEarningsLeaderboard(dateType)
        if (requestId !== this.leaderboardRequestId) return
        this.leaderboardLeaders = result.leaders
        this.leaderboardRank = result.rank && typeof result.rank === 'object'
          ? result.rank
          : null
        this.leaderboardErrors = result.errors
      } finally {
        if (requestId === this.leaderboardRequestId) this.leaderboardLoading = false
      }
    },
    openSupport(page) {
      if (page === 'help') {
        this.$root.$emit('open-help-center')
        return
      }
      if (page === 'tutorial') {
        this.$root.$emit('open-help-center', 'tutorial')
      }
    },
    async loadStrategyListSnapshot(mode, robotType, page = 1) {
      if (mode === 'history') {
        return loadExchangeHistoryStrategies(
          robotType,
          page,
          this.historyStrategyPagination.pageSize
        )
      }
      return loadExchangeStrategies(robotType)
    },
    applyStrategyListSnapshot(snapshot, mode) {
      const rows = Array.isArray(snapshot && snapshot.strategies)
        ? snapshot.strategies
        : []
      const page = mode === 'history' ? Number(snapshot.page) || 1 : 1
      const pageSize = mode === 'history'
        ? Number(snapshot.pageSize) || this.historyStrategyPagination.pageSize
        : rows.length
      const total = mode === 'history'
        ? Math.max(0, Number(snapshot.total) || 0)
        : rows.length
      const rankStart = mode === 'history'
        ? Math.max(rows.length, total - ((page - 1) * pageSize))
        : rows.length
      this.robots = rows.map((item, index) => mapRobot(item, index, rankStart))
      if (mode === 'history') {
        this.historyStrategyPagination = { page, pageSize, total }
      }
    },
    async refreshVisibleStrategies() {
      if (this.selectedRobotType === null) {
        this.robots = []
        return
      }
      const mode = this.strategyListMode === 'history' ? 'history' : 'current'
      const page = mode === 'history' ? this.historyStrategyPagination.page : 1
      const requestId = ++this.strategyListRequestId
      this.strategyListLoading = true
      try {
        const snapshot = await this.loadStrategyListSnapshot(
          mode,
          this.selectedRobotType,
          page
        )
        if (requestId !== this.strategyListRequestId || this.strategyListMode !== mode) return
        this.applyStrategyListSnapshot(snapshot, mode)
        const errors = Array.isArray(snapshot.strategyErrors) ? snapshot.strategyErrors : []
        if (errors.length) this.showToast(errors[0], 'error')
        this.lastSync = new Date()
      } catch (error) {
        if (requestId === this.strategyListRequestId) {
          this.showToast(error.message || this.$t('策略列表读取失败'), 'error')
        }
      } finally {
        if (requestId === this.strategyListRequestId) this.strategyListLoading = false
      }
    },
    async handleStrategyModeChange(mode) {
      const nextMode = mode === 'history' ? 'history' : 'current'
      this.strategyListMode = nextMode
      this.robots = []
      if (nextMode === 'history') this.historyStrategyPagination.page = 1
      if (this.selectedRobotType === null) {
        this.robots = []
        return
      }
      const requestId = ++this.strategyListRequestId
      this.strategyListLoading = true
      try {
        const snapshot = await this.loadStrategyListSnapshot(
          nextMode,
          this.selectedRobotType,
          1
        )
        if (requestId !== this.strategyListRequestId || this.strategyListMode !== nextMode) return
        this.applyStrategyListSnapshot(snapshot, nextMode)
        const errors = Array.isArray(snapshot.strategyErrors) ? snapshot.strategyErrors : []
        if (errors.length) this.showToast(errors[0], 'error')
        this.lastSync = new Date()
      } catch (error) {
        if (requestId === this.strategyListRequestId) {
          this.showToast(error.message || this.$t('策略列表读取失败'), 'error')
        }
      } finally {
        if (requestId === this.strategyListRequestId) this.strategyListLoading = false
      }
    },
    async handleHistoryStrategyPageChange(page) {
      if (this.strategyListMode !== 'history' || this.selectedRobotType === null) return
      const totalPages = Math.max(
        1,
        Math.ceil(this.historyStrategyPagination.total / this.historyStrategyPagination.pageSize)
      )
      const nextPage = Math.min(totalPages, Math.max(1, Number(page) || 1))
      const requestId = ++this.strategyListRequestId
      this.strategyListLoading = true
      try {
        const snapshot = await this.loadStrategyListSnapshot(
          'history',
          this.selectedRobotType,
          nextPage
        )
        if (requestId !== this.strategyListRequestId || this.strategyListMode !== 'history') return
        this.applyStrategyListSnapshot(snapshot, 'history')
        const errors = Array.isArray(snapshot.strategyErrors) ? snapshot.strategyErrors : []
        if (errors.length) this.showToast(errors[0], 'error')
        this.lastSync = new Date()
      } catch (error) {
        if (requestId === this.strategyListRequestId) {
          this.showToast(error.message || this.$t('历史策略读取失败'), 'error')
        }
      } finally {
        if (requestId === this.strategyListRequestId) this.strategyListLoading = false
      }
    },
    async handleExchangeChange(command) {
      const nextRobotType = Number(command)
      if (!Number.isFinite(nextRobotType) || nextRobotType === this.selectedRobotType) return
      const requestId = ++this.snapshotRequestId
      this.selectedRobotType = nextRobotType
      this.exchange = exchangeName(this.selectedRobotType)
      this.exchangeSwitching = true
      this.loading = true
      try {
        let robotInfoError = null
        try {
          const robotInfo = await loadRobotPowerState(nextRobotType)
          if (requestId !== this.snapshotRequestId) return
          this.applyRobotInfo(nextRobotType, robotInfo)
        } catch (error) {
          robotInfoError = error
        }
        if (requestId !== this.snapshotRequestId) return

        const shouldLoadRisk = this.activeSection === 'overview'
        const shouldLoadEarnings = this.activeSection === 'earnings' &&
          this.isMobileViewport() &&
          !this.isEarningsLeaderboard
        if (this.strategyListMode === 'history') this.historyStrategyPagination.page = 1
        const [exchangeSnapshot, riskResult] = await Promise.all([
          this.loadStrategyListSnapshot(this.strategyListMode, nextRobotType, 1),
          shouldLoadRisk
            ? loadRobotRisk(nextRobotType)
            : Promise.resolve(null),
          shouldLoadEarnings
            ? this.loadEarningsDetailsForExchange(nextRobotType)
            : Promise.resolve(null)
        ])
        if (requestId !== this.snapshotRequestId) return
        this.applyStrategyListSnapshot(exchangeSnapshot, this.strategyListMode)
        if (shouldLoadRisk) {
          this.robotRisk = riskResult && riskResult.data &&
            typeof riskResult.data === 'object'
            ? riskResult.data
            : null
          this.robotRiskRobotType = nextRobotType
        } else {
          this.robotRisk = null
          this.robotRiskRobotType = null
        }
        this.loadErrors = [
          ...(robotInfoError ? [robotInfoError.message || this.$t('机器人状态读取失败')] : []),
          ...(riskResult && riskResult.error ? [riskResult.error] : []),
          ...exchangeSnapshot.strategyErrors
        ]
        this.lastSync = new Date()

        if (exchangeSnapshot.strategyErrors.length) {
          this.showToast(exchangeSnapshot.strategyErrors[0], 'error')
          return
        }
        if (robotInfoError) {
          this.showToast(robotInfoError.message || this.$t('机器人状态读取失败'), 'error')
          return
        }
        this.showToast(this.$t(`${this.exchange} 策略数据已同步`), 'success')
      } finally {
        if (requestId === this.snapshotRequestId) this.loading = false
        this.exchangeSwitching = false
      }
    },
    async loadEarningsDetailsForExchange(robotType = this.selectedRobotType) {
      const normalizedRobotType = Number(robotType)
      if (!Number.isFinite(normalizedRobotType)) return
      const requestId = ++this.earningsDetailsRequestId
      this.earningsDetailsLoading = true
      try {
        const result = await loadEarningsDetails(normalizedRobotType)
        if (
          requestId !== this.earningsDetailsRequestId ||
          Number(this.selectedRobotType) !== normalizedRobotType
        ) return
        this.earnings = {
          ...this.earnings,
          distribution: result.distribution,
          records: result.records
        }
        this.earningsDetailsLoadedRobotType = normalizedRobotType
        if (result.errors.length) this.showToast(result.errors[0], 'error')
      } catch (error) {
        if (requestId === this.earningsDetailsRequestId) {
          this.showToast(error.message || this.$t('收益详情读取失败'), 'error')
        }
      } finally {
        if (requestId === this.earningsDetailsRequestId) this.earningsDetailsLoading = false
      }
    },    async loadOverviewRobotRisk(robotType = this.selectedRobotType) {
      const normalizedRobotType = Number(robotType)
      if (!Number.isFinite(normalizedRobotType) || this.robotRiskLoading) return
      this.robotRiskLoading = true
      try {
        const result = await loadRobotRisk(normalizedRobotType)
        if (Number(this.selectedRobotType) !== normalizedRobotType) return
        this.robotRisk = result.data && typeof result.data === 'object'
          ? result.data
          : null
        this.robotRiskRobotType = normalizedRobotType
        if (result.error && !this.loadErrors.includes(result.error)) {
          this.loadErrors = [...this.loadErrors, result.error]
        }
      } finally {
        this.robotRiskLoading = false
      }
    },
    async openAction(payload) {
      const type = payload && typeof payload === 'object' ? payload.type : payload
      if (type === 'deposit') {
        this.resetDepositFlow()
        this.depositVisible = true
        await this.reloadDepositMethods()
        return
      }
      if (type === 'withdraw') {
        this.withdrawalTypeVisible = true
        return
      }
      if (type === 'transfer') {
        const sourceAccount = payload && typeof payload === 'object'
          ? payload.account
          : ''
        this.actionTransferFrom = sourceAccount === 'fuel' ? '2' : '1'
      }
      this.actionType = type
      this.actionVisible = true
      if (type === 'buy-card') {
        await this.reloadPointCardOptions()
      }
    },
    openWithdrawalAction(type) {
      if (!['withdraw', 'internal-withdraw'].includes(type)) return
      this.withdrawalTypeVisible = false
      this.actionType = type
      this.$nextTick(() => {
        this.actionVisible = true
      })
    },
    async reloadPointCardOptions() {
      if (this.pointCardsLoading) return
      this.pointCards = []
      this.pointCardsLoading = true
      try {
        const result = await loadPointCardOptions()
        this.pointCards = result.data
        if (result.error) this.showToast(result.error, 'error')
      } finally {
        this.pointCardsLoading = false
      }
    },
    async reloadDepositMethods() {
      if (this.depositMethodsLoading) return
      this.depositMethods = []
      this.depositMethodsLoading = true
      try {
        const result = await loadRechargeMethods()
        this.depositMethods = result.data
        if (result.error) this.showToast(result.error, 'error')
      } finally {
        this.depositMethodsLoading = false
      }
    },
    async handleRobotPower() {
      if (this.selectedRobotType === null) {
        this.showToast(this.$t('当前没有可操作的交易所机器人'), 'error')
        return
      }
      const robot = this.cachedRobotPowerState(this.selectedRobotType)
      if (!robot) {
        this.showToast(this.$t(`${this.exchange} 机器人状态尚未加载`), 'error')
        return
      }
      if (Number(robot.bind_api) !== 1) {
        this.showToast(this.$t(`请先绑定 ${robot.exchange} 交易所 API`), 'error')
        return
      }
      if (!this.robots.length) {
        const opened = await this.openStrategyCreate()
        if (opened) this.showToast(this.$t('添加完策略才能启动机器人进行交易哦~'), 'info')
        return
      }
      this.robotPowerSubmitting = true
      try {
        if (Number(robot.state) === 1) {
          this.openRobotPowerConfirm([robot], false)
          return
        }
        await this.startRobotPowerRows([robot], false)
      } catch (error) {
        this.showToast(error.message || this.$t('机器人操作失败，请稍后重试'), 'error')
      } finally {
        this.robotPowerSubmitting = false
      }
    },
    cachedRobotPowerState(robotType) {
      const normalizedRobotType = Number(robotType)
      const robotInfo = this.exchangeRobots.find(item =>
        Number(item && item.robot_type) === normalizedRobotType
      )
      if (!robotInfo) return null
      return {
        ...robotInfo,
        robotType: normalizedRobotType,
        exchange: exchangeName(normalizedRobotType)
      }
    },
    async readRobotPowerState(robotType) {
      const robotInfo = await loadRobotPowerState(robotType)
      const robot = {
        ...robotInfo,
        robotType: Number(robotType),
        exchange: exchangeName(robotType)
      }
      if (Number(robot.bind_api) !== 1) {
        throw new Error(this.$t(`${robot.exchange} 尚未绑定交易所 API`))
      }
      return robot
    },
    openRobotPowerConfirm(robots, allExchanges) {
      this.robotPowerPending = { robots, allExchanges }
      this.robotPowerConfirmVisible = true
    },
    async startRobotPowerRows(robots, allExchanges) {
      if (!robots.length) {
        throw new Error(this.$t('当前没有可以启动的机器人'))
      }
      const failures = []
      const startedRobotTypes = []
      let successCount = 0
      for (const robot of robots) {
        try {
          const current = await this.readRobotPowerState(robot.robotType)
          if (Number(current.state) !== 2) continue
          const response = await executeRobotPower(robot.robotType, current.state)
          this.ensureSuccess(response.result, `${robot.exchange} 机器人启动失败`)
          startedRobotTypes.push(Number(robot.robotType))
          successCount += 1
        } catch (error) {
          failures.push(error.message || this.$t(`${robot.exchange} 启动失败`))
        }
      }
      this.applyRobotPowerState(startedRobotTypes, 1)
      if (failures.length) {
        throw new Error(
          this.$t(`${successCount ? `已启动 ${successCount} 个；` : ''}${failures.join('；')}`)
        )
      }
      this.showToast(
        this.$t(allExchanges
          ? `机器人已启动，${this.robots.length} 台策略开始运行`
          : `${robots[0].exchange} 机器人已启动`),
        'success'
      )
    },
    async confirmRobotPowerClose() {
      const pending = this.robotPowerPending
      if (!pending || !pending.robots.length) return
      this.robotPowerSubmitting = true
      const failures = []
      const closedRobotTypes = []
      let successCount = 0
      try {
        for (const robot of pending.robots) {
          try {
            if (Number(robot.state) !== 1) continue
            const response = await executeRobotPower(robot.robotType, robot.state)
            this.ensureSuccess(response.result, `${robot.exchange} 机器人关闭失败`)
            closedRobotTypes.push(Number(robot.robotType))
            successCount += 1
          } catch (error) {
            failures.push(error.message || this.$t(`${robot.exchange} 关闭失败`))
          }
        }
        this.robotPowerConfirmVisible = false
        this.robotPowerPending = null
        this.applyRobotPowerState(closedRobotTypes, 2)
        if (failures.length) {
          throw new Error(
            this.$t(`${successCount ? `已关闭 ${successCount} 个；` : ''}${failures.join('；')}`)
          )
        }
        this.showToast(
          this.$t(pending.allExchanges
            ? '机器人已关闭，全部运行中的策略已暂停'
            : `${pending.robots[0].exchange} 机器人已关闭`),
          'info'
        )
      } catch (error) {
        this.showToast(error.message || this.$t('机器人关闭失败，请稍后重试'), 'error')
      } finally {
        this.robotPowerSubmitting = false
      }
    },
    applyRobotPowerState(robotTypes, state) {
      const targets = new Set(
        robotTypes
          .map(robotType => Number(robotType))
          .filter(Number.isFinite)
      )
      if (!targets.size) return
      this.exchangeRobots = this.exchangeRobots.map(robotInfo => {
        const robotType = Number(
          robotInfo && (
            robotInfo.robot_type !== undefined
              ? robotInfo.robot_type
              : robotInfo.robotType
          )
        )
        if (!targets.has(robotType)) return robotInfo
        return {
          ...robotInfo,
          state: Number(state)
        }
      })
    },
    applyRobotInfo(robotType, robotInfo) {
      const normalizedRobotType = Number(robotType)
      if (!Number.isFinite(normalizedRobotType) || !robotInfo) return
      this.exchangeRobots = this.exchangeRobots.map(item => {
        if (Number(item && item.robot_type) !== normalizedRobotType) return item
        return {
          ...item,
          ...robotInfo,
          robot_type: normalizedRobotType
        }
      })
    },
    openRobot(robot) {
      if (!robot || robot.id === null || robot.id === undefined) {
        this.showToast(this.$t('该策略暂时无法进入详情'), 'error')
        return
      }
      this.detailRobotCache = robot
      sessionStorage.setItem('uplusStrategyDetail', JSON.stringify(robot))
      this.$router.push({
        name: 'strategy-detail',
        params: { strategyId: String(robot.id) },
        query: {
          robotType: String(robotTypeValue(robot)),
          ...(robot.raw && robot.raw.__historyStrategy ? { history: '1' } : {})
        }
      }).catch(error => {
        if (!error || error.name !== 'NavigationDuplicated') {
          this.showToast(this.$t('策略详情打开失败'), 'error')
        }
      })
    },
    closeStrategyDetail() {
      if (this.$route.path !== '/app/robots') {
        this.$router.push('/app/robots')
      }
    },
    handleDetailToast({ message, type }) {
      this.showToast(message, type)
    },
    async openRobotAction({ action, robot }) {
      if (robotProductType(robot) === 'orbit') return this.openOrbitAction({ action, robot })
      const scopedRobot = {
        ...robot,
        raw: {
          ...(robot.raw || {}),
          __robotState: this.activeExchangeRobot && this.activeExchangeRobot.state
        }
      }
      if (
        action === 'modify' &&
        robotProductType(scopedRobot) === 'term' &&
        String(scopedRobot.raw.version_number) === '2.0.0'
      ) {
        this.showToast(this.$t('请先删除当前仓位后再修改策略'), 'error')
        return
      }
      if (action === 'resume' && robotNeedsMaximumRestart(scopedRobot)) {
        this.robotExceptionRobot = scopedRobot
        this.robotExceptionPhase = 'modify'
        this.robotExceptionVisible = true
        return
      }
      try {
        const target = action === 'modify'
          ? await loadRobotActionDetail(scopedRobot)
          : scopedRobot
        this.robotActionType = action
        this.robotActionRobot = target
        this.robotActionVisible = true
      } catch (error) {
        this.showToast(error.message || this.$t('策略数据读取失败，请稍后重试'), 'error')
      }
    },
    openRobotException(robot) {
      this.robotExceptionRobot = robot
      this.robotExceptionPhase = 'warning'
      this.robotExceptionVisible = true
    },
    openRobotExceptionModify() {
      this.robotExceptionPhase = 'modify'
    },
    async handleRobotExceptionSubmit({ robot, form }) {
      this.robotExceptionSubmitting = true
      try {
        const result = await executeRobotAction({
          action: 'resume',
          robot,
          form
        })
        this.ensureSuccess(result, '修改最大持仓并重启策略失败')
        this.robotExceptionVisible = false
        this.applyLocalRobotStatus(robot, 'running')
        this.showToast(this.$t('最大持仓修改成功，策略已重新启动'), 'success')
      } catch (error) {
        this.showToast(error.message || this.$t('策略重启失败，请稍后重试'), 'error')
      } finally {
        this.robotExceptionSubmitting = false
      }
    },
    openStrategyCreate(productKey = 'astra') {
      // 对齐 App bot.vue 的 gotoAuto：冻结账号必须在所有策略判断之前拦截。
      if (Number(this.userInfo && this.userInfo.is_stop) === 1) {
        this.showToast(
          this.$t('系统检测你的账号存在异常，为了你的隐私和资金安全，系统已保护冻结账号。如需解冻，请联系客服。'),
          'error'
        )
        return false
      }
      if (this.selectedRobotType === null) {
        this.showToast(this.$t('当前没有可添加策略的交易所'), 'error')
        return false
      }
      const robot = this.cachedRobotPowerState(this.selectedRobotType)
      if (!robot) {
        this.showToast(this.$t(`${this.exchange} 机器人状态尚未加载，暂不能添加策略`), 'error')
        return false
      }
      if (Number(robot.bind_api) !== 1) {
        this.showToast(
          this.$t(`请先至“交易所 API 授权”页面，绑定 ${robot.exchange} 交易所 API`),
          'error'
        )
        return false
      }

      const product = getStrategyProduct(productKey)
      if (!product.exchanges.includes(Number(this.selectedRobotType))) {
        this.showToast(this.$t(`${this.exchange} 暂不支持创建 ${product.shortName || product.name}`), 'error')
        return false
      }
      if (product.key !== 'orbit') {
        if (!this.strategyCapacity || typeof this.strategyCapacity !== 'object') {
          this.showToast(this.$t('机器人页面尚未加载策略可用名额，暂不能创建策略'), 'error')
          return false
        }
        const availabilityIssue = strategyAvailabilityIssue(product.key, this.strategyCapacity)
        if (availabilityIssue) {
          this.showToast(availabilityIssue, 'error')
          return false
        }
      }

      this.strategyProductKey = product.key
      this.strategyRiskApproved = false
      this.strategyRiskVisible = true
      return true
    },
    confirmStrategyRisk() {
      this.strategyRiskApproved = true
      this.strategyRiskVisible = false
    },
    openStrategyAgreement() {
      this.strategyAgreementVisible = true
    },
    closeStrategyAgreement() {
      this.strategyAgreementVisible = false
    },
    handleStrategyRiskClosed() {
      if (!this.strategyRiskApproved) return
      this.strategyRiskApproved = false
      if (this.strategyProductKey === 'orbit') {
        this.orbitStrategyCreateVisible = true
        return
      }
      this.strategyCreateVisible = true
    },
    async fetchSnapshot() {
      const requestId = ++this.snapshotRequestId
      this.loading = true
      const snapshot = await loadPortalSnapshot(this.selectedRobotType)
      if (requestId !== this.snapshotRequestId) return
      const incomingUser = snapshot.user && typeof snapshot.user === 'object'
        ? snapshot.user
        : null
      if (incomingUser && Object.keys(incomingUser).length) {
        this.userInfo = { ...this.userInfo, ...incomingUser }
        localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
      }
      this.exchangeListError = snapshot.exchangeError || ''
      const activeRobotType = snapshot.activeRobotType === null ||
        snapshot.activeRobotType === undefined
        ? null
        : Number(snapshot.activeRobotType)
      if (activeRobotType !== null && Number.isFinite(activeRobotType)) {
        this.selectedRobotType = activeRobotType
        this.exchange = exchangeName(activeRobotType)
      } else {
        this.selectedRobotType = null
        this.exchange = this.exchangeListError ? '交易所加载失败' : '暂无可用交易所'
      }
      this.applyUserBalances(this.userInfo)
      this.applyEarnings(snapshot)
      if (this.strategyListMode === 'current') {
        this.robots = snapshot.strategies.map((item, index) =>
          mapRobot(item, index, snapshot.strategies.length)
        )
      }
      this.exchangeRobots = snapshot.exchangeRobots
      this.strategyCapacity = snapshot.strategyCapacity &&
        typeof snapshot.strategyCapacity === 'object'
        ? snapshot.strategyCapacity
        : null
      this.leaders = snapshot.leaders
        .slice(0, 10)
        .map((item, index) => mapLeader(item, index, this.userInfo))
      this.rate = finiteNumber(snapshot.rate)
      this.earningsPeriod = snapshot.period
      this.loadErrors = snapshot.errors
      this.lastSync = new Date()
      this.loading = false
      return snapshot
    },
    async fetchAssetRecords() {
      this.recordLoading = true
      try {
        const records = await loadAssetRecords()
        this.assetRecords = records
        this.assetOrderPagination = {
          deposit: {
            page: 1,
            pageSize: 10,
            total: Number(records.rechargeTotal) || 0
          },
          withdraw: {
            page: 1,
            pageSize: 10,
            total: Number(records.withdrawalTotal) || 0
          }
        }
        this.assetRecordsLoaded = true
      } finally {
        this.recordLoading = false
      }
    },
    async fetchBillRecordPage(page = 1, period = this.billRecordPeriod) {
      if (this.billRecordLoading) return
      const normalizedPage = Math.max(1, Number(page) || 1)
      const normalizedPeriod = {
        year: Number(period && period.year) || Number(this.billRecordPeriod.year),
        month: Number(period && period.month) || Number(this.billRecordPeriod.month)
      }
      this.billRecordLoading = true
      try {
        let result = await loadDepositRecords(normalizedPage, 10, normalizedPeriod)
        if (result.error) {
          this.showToast(result.error, 'error')
          return
        }
        const total = Math.max(0, Number(result.total) || 0)
        const lastPage = total > 0 ? Math.ceil(total / 10) : 1
        const resolvedPage = Math.min(normalizedPage, lastPage)
        if (resolvedPage !== normalizedPage) {
          result = await loadDepositRecords(resolvedPage, 10, normalizedPeriod)
          if (result.error) {
            this.showToast(result.error, 'error')
            return
          }
        }
        this.billRecords = result.rows
        this.billRecordPagination = {
          page: resolvedPage,
          pageSize: 10,
          total: Number(result.total) || 0
        }
        this.billRecordSummary = {
          allDeposit: finiteNumber(result.allDeposit),
          previousAllDeposit: finiteNumber(result.previousAllDeposit)
        }
        this.billRecordLoaded = true
      } finally {
        this.billRecordLoading = false
        if (this.billRecordPendingPeriod) {
          const pendingPeriod = this.billRecordPendingPeriod
          this.billRecordPendingPeriod = null
          this.billRecordLoaded = false
          await this.fetchBillRecordPage(1, pendingPeriod)
        }
      }
    },
    async changeBillRecordPage({ page } = {}) {
      const normalizedPage = Math.max(1, Number(page) || 1)
      if (
        this.billRecordLoading ||
        normalizedPage === Number(this.billRecordPagination.page)
      ) return
      await this.fetchBillRecordPage(normalizedPage, this.billRecordPeriod)
    },
    async changeBillRecordPeriod(period = {}) {
      const year = Number(period.year)
      const month = Number(period.month)
      if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) return
      const normalizedPeriod = { year, month }
      this.billRecordPeriod = normalizedPeriod
      this.billRecords = []
      this.billRecordLoaded = false
      this.billRecordPagination = { page: 1, pageSize: 10, total: 0 }
      this.billRecordSummary = { allDeposit: null }
      if (this.billRecordLoading) {
        this.billRecordPendingPeriod = normalizedPeriod
        return
      }
      await this.fetchBillRecordPage(1, normalizedPeriod)
    },
    async handleBillTabChange({ tab, category } = {}) {
      if (tab === 'flow') {
        await this.ensureBillFlowRecords(category)
        return
      }
      if (tab === 'account') {
        await this.ensureBillAccountRecords(category)
      }
    },
    async changeBillFlowCategory({ category } = {}) {
      await this.ensureBillFlowRecords(category)
    },
    async changeBillFlowPage({ category, page } = {}) {
      const normalizedCategory = category === 'withdraw' ? 'withdraw' : 'deposit'
      const normalizedPage = Math.max(1, Number(page) || 1)
      const current = this.billOrderPagination[normalizedCategory] || {}
      if (
        this.billFlowLoading ||
        normalizedPage === Number(current.page)
      ) return
      await this.fetchBillFlowPage(normalizedCategory, normalizedPage)
    },
    async ensureBillFlowRecords(category) {
      const normalizedCategory = category === 'withdraw' ? 'withdraw' : 'deposit'
      if (this.billFlowLoaded[normalizedCategory] || this.billFlowLoading) return
      await this.fetchBillFlowPage(normalizedCategory, 1)
    },
    async changeBillAccountCategory({ category } = {}) {
      await this.ensureBillAccountRecords(category)
    },
    async changeBillAccountPage({ category, page } = {}) {
      const normalizedCategory = category === 'fuelTransfer' ? 'fuelTransfer' : 'pointCard'
      const normalizedPage = Math.max(1, Number(page) || 1)
      const current = this.billAccountPagination[normalizedCategory] || {}
      if (
        this.billAccountLoading ||
        normalizedPage === Number(current.page)
      ) return
      await this.fetchBillAccountPage(normalizedCategory, normalizedPage)
    },
    async ensureBillAccountRecords(category) {
      const normalizedCategory = category === 'fuelTransfer' ? 'fuelTransfer' : 'pointCard'
      if (this.billAccountLoaded[normalizedCategory] || this.billAccountLoading) return
      await this.fetchBillAccountPage(normalizedCategory, 1)
    },
    async fetchBillAccountPage(category, page) {
      if (this.billAccountLoading) return
      const normalizedCategory = category === 'fuelTransfer' ? 'fuelTransfer' : 'pointCard'
      const normalizedPage = Math.max(1, Number(page) || 1)
      this.billAccountLoading = true
      try {
        let result
        if (normalizedCategory === 'pointCard' && this.billAccountLoaded.pointCard) {
          result = {
            rows: this.billPointCardSource,
            total: this.billPointCardSource.length,
            error: null
          }
        } else {
          result = await loadCardTransferRecords(normalizedCategory, normalizedPage, 10)
        }
        if (result.error) {
          this.showToast(result.error, 'error')
          return
        }
        if (normalizedCategory === 'pointCard') {
          this.billPointCardSource = Array.isArray(result.rows) ? result.rows : []
        }
        const total = Math.max(0, Number(result.total) || 0)
        const lastPage = total > 0 ? Math.ceil(total / 10) : 1
        const resolvedPage = Math.min(normalizedPage, lastPage)
        if (normalizedCategory === 'fuelTransfer' && resolvedPage !== normalizedPage) {
          result = await loadCardTransferRecords(normalizedCategory, resolvedPage, 10)
          if (result.error) {
            this.showToast(result.error, 'error')
            return
          }
        }
        const rows = normalizedCategory === 'pointCard'
          ? this.billPointCardSource.slice((resolvedPage - 1) * 10, resolvedPage * 10)
          : result.rows
        this.billAccountRecords = {
          ...this.billAccountRecords,
          [normalizedCategory]: rows
        }
        this.billAccountPagination = {
          ...this.billAccountPagination,
          [normalizedCategory]: {
            page: resolvedPage,
            pageSize: 10,
            total: Number(result.total) || 0
          }
        }
        this.billAccountLoaded = {
          ...this.billAccountLoaded,
          [normalizedCategory]: true
        }
      } finally {
        this.billAccountLoading = false
      }
    },
    async refreshRechargeRecords() {
      const result = await loadAssetOrderRecords('deposit', 1, 10)
      if (result.error) {
        this.showToast(result.error, 'error')
        return
      }
      this.assetRecords = {
        ...this.assetRecords,
        recharges: result.rows,
        rechargeTotal: result.total
      }
      this.assetOrderPagination = {
        ...this.assetOrderPagination,
        deposit: {
          page: 1,
          pageSize: 10,
          total: result.total
        }
      }
      this.billFlowRecords = {
        ...this.billFlowRecords,
        recharges: result.rows,
        rechargeTotal: result.total
      }
      this.billOrderPagination = {
        ...this.billOrderPagination,
        deposit: {
          page: 1,
          pageSize: 10,
          total: result.total
        }
      }
      this.billFlowLoaded = {
        ...this.billFlowLoaded,
        deposit: true
      }
    },
    async refreshAssetOrderRecords({ category, page } = {}) {
      const normalizedCategory = category === 'withdraw' ? 'withdraw' : 'deposit'
      const current = this.assetOrderPagination[normalizedCategory] || {}
      const normalizedPage = Math.max(1, Number(page || current.page) || 1)
      await this.fetchAssetOrderPage(normalizedCategory, normalizedPage)
    },
    async changeAssetOrderPage({ category, page }) {
      const normalizedCategory = category === 'withdraw' ? 'withdraw' : 'deposit'
      const normalizedPage = Math.max(1, Number(page) || 1)
      const current = this.assetOrderPagination[normalizedCategory]
      if (this.recordLoading || (current && current.page === normalizedPage)) return

      await this.fetchAssetOrderPage(normalizedCategory, normalizedPage)
    },
    async fetchAssetOrderPage(category, page) {
      if (this.recordLoading) return
      const normalizedCategory = category === 'withdraw' ? 'withdraw' : 'deposit'
      const normalizedPage = Math.max(1, Number(page) || 1)
      this.recordLoading = true
      try {
        let result = await loadAssetOrderRecords(normalizedCategory, normalizedPage, 10)
        if (result.error) {
          this.showToast(result.error, 'error')
          return
        }
        const total = Math.max(0, Number(result.total) || 0)
        const lastPage = total > 0 ? Math.ceil(total / 10) : 1
        const resolvedPage = Math.min(normalizedPage, lastPage)
        if (resolvedPage !== normalizedPage) {
          result = await loadAssetOrderRecords(normalizedCategory, resolvedPage, 10)
          if (result.error) {
            this.showToast(result.error, 'error')
            return
          }
        }
        const rowsKey = normalizedCategory === 'withdraw' ? 'withdrawals' : 'recharges'
        const totalKey = normalizedCategory === 'withdraw' ? 'withdrawalTotal' : 'rechargeTotal'
        this.assetRecords = {
          ...this.assetRecords,
          [rowsKey]: result.rows,
          [totalKey]: result.total
        }
        this.assetOrderPagination = {
          ...this.assetOrderPagination,
          [normalizedCategory]: {
            page: resolvedPage,
            pageSize: 10,
            total: result.total
          }
        }
        this.assetRecordsLoaded = true
      } finally {
        this.recordLoading = false
      }
    },
    async fetchBillFlowPage(category, page) {
      if (this.billFlowLoading) return
      const normalizedCategory = category === 'withdraw' ? 'withdraw' : 'deposit'
      const normalizedPage = Math.max(1, Number(page) || 1)
      this.billFlowLoading = true
      try {
        let result = await loadAssetOrderRecords(normalizedCategory, normalizedPage, 10)
        if (result.error) {
          this.showToast(result.error, 'error')
          return
        }
        const total = Math.max(0, Number(result.total) || 0)
        const lastPage = total > 0 ? Math.ceil(total / 10) : 1
        const resolvedPage = Math.min(normalizedPage, lastPage)
        if (resolvedPage !== normalizedPage) {
          result = await loadAssetOrderRecords(normalizedCategory, resolvedPage, 10)
          if (result.error) {
            this.showToast(result.error, 'error')
            return
          }
        }
        const rowsKey = normalizedCategory === 'withdraw' ? 'withdrawals' : 'recharges'
        const totalKey = normalizedCategory === 'withdraw' ? 'withdrawalTotal' : 'rechargeTotal'
        this.billFlowRecords = {
          ...this.billFlowRecords,
          [rowsKey]: result.rows,
          [totalKey]: result.total
        }
        this.billOrderPagination = {
          ...this.billOrderPagination,
          [normalizedCategory]: {
            page: resolvedPage,
            pageSize: 10,
            total: result.total
          }
        }
        this.billFlowLoaded = {
          ...this.billFlowLoaded,
          [normalizedCategory]: true
        }
      } finally {
        this.billFlowLoading = false
      }
    },
    async refreshAssetSummary() {
      if (this.assetRefreshing) return
      this.assetRefreshing = true
      try {
        const result = await loadAssetSummary()
        if (result.user && typeof result.user === 'object') {
          this.userInfo = { ...this.userInfo, ...result.user }
          localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
          this.applyUserBalances(this.userInfo)
        }
        const refreshedRate = finiteNumber(result.rate)
        if (refreshedRate !== null) this.rate = refreshedRate
        this.lastSync = new Date()
        if (result.errors.length) this.showToast(result.errors[0], 'error')
      } finally {
        this.assetRefreshing = false
      }
    },
    async refreshAccountBalances() {
      const result = await loadAccountBalances()
      if (result.user && typeof result.user === 'object') {
        this.userInfo = { ...this.userInfo, ...result.user }
        localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
        this.applyUserBalances(this.userInfo)
      }
      this.lastSync = new Date()
      if (result.errors.length) this.showToast(result.errors[0], 'error')
    },
    applyUserBalances(user) {
      const fund = firstNumber(user, ['asset_balance'])
      const fuel = firstNumber(user, ['deposit'])
      const card = firstNumber(user, ['fuel_card'])
      const values = [fund, fuel, card].filter(value => value !== null)
      this.balances = {
        fund,
        fuel,
        card,
        total: values.length ? values.reduce((sum, value) => sum + value, 0) : null
      }
    },
    applyEarnings(snapshot) {
      const profit = snapshot.profit && typeof snapshot.profit === 'object' ? snapshot.profit : {}
      const todayValues = snapshot.today
        .map(item => firstNumber(item, ['profit', 'trade_profit']))
        .filter(value => value !== null)
      const todayYieldValues = snapshot.today
        .map(item => firstNumber(item, ['yield', 'trade_profit_yield', 'profit_yield']))
        .filter(value => value !== null)
      const monthValues = snapshot.monthTrend
        .map(item => firstNumber(item, ['profit', 'trade_profit']))
        .filter(value => value !== null)
      const lastMonthRow = snapshot.monthTrend[snapshot.monthTrend.length - 1] || {}
      this.earnings = {
        profit: firstNumber(profit, ['profit']),
        yield: firstNumber(profit, ['yield']),
        avgYield: firstNumber(profit, ['avg_yield']),
        todayProfit: todayValues.length ? todayValues.reduce((sum, value) => sum + value, 0) : null,
        todayYield: todayYieldValues.length
          ? todayYieldValues.reduce((sum, value) => sum + value, 0)
          : null,
        monthProfit: monthValues.length ? monthValues.reduce((sum, value) => sum + value, 0) : null,
        monthYield: firstNumber(lastMonthRow, ['month_yield']),
        monthRows: snapshot.monthTrend,
        allRows: snapshot.allTrend,
        profitLine: snapshot.profitLine,
        rank: snapshot.rank,
        distribution: this.earnings.distribution || {},
        records: this.earnings.records || []
      }
    },
    async changeEarningsPeriod(period) {
      const year = Number(period && period.year)
      const month = Number(period && period.month)
      if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) return
      const requestId = ++this.earningsPeriodRequestId
      this.earningsPeriodLoading = true
      try {
        const result = await loadEarningsMonth(year, month)
        if (requestId !== this.earningsPeriodRequestId) return
        if (result.error) {
          this.earningsPeriod = { year, month }
          this.earnings = {
            ...this.earnings,
            monthRows: [],
            monthProfit: null,
            monthYield: null
          }
          this.showToast(result.error, 'error')
          return
        }
        const values = result.rows
          .map(item => firstNumber(item, ['profit', 'trade_profit']))
          .filter(value => value !== null)
        const last = result.rows[result.rows.length - 1] || {}
        this.earningsPeriod = { year, month }
        this.earnings = {
          ...this.earnings,
          monthRows: result.rows,
          monthProfit: values.length ? values.reduce((sum, value) => sum + value, 0) : null,
          monthYield: firstNumber(last, ['month_yield'])
        }
      } finally {
        if (requestId === this.earningsPeriodRequestId) this.earningsPeriodLoading = false
      }
    },
    async handleDepositSubmit(payload) {
      this.depositSubmitting = true
      try {
        const result = await createRechargeOrder(payload.amount, payload.payType)
        this.ensureSuccess(result, '充值订单创建失败')

        const data = result.data && typeof result.data === 'object' ? result.data : {}
        const addressInfo = data.addressInfo && typeof data.addressInfo === 'object'
          ? data.addressInfo
          : {}
        const chainDetail = String(addressInfo.chain_detail || payload.method || '')
        const matchedMethod = chainDetail.match(/\(([^)]+)\)/)

        this.depositOrder = {
          address: data.address || '',
          time: data.time || '',
          money: data.money === null || data.money === undefined ? payload.amount : data.money,
          method: matchedMethod ? matchedMethod[1] : chainDetail
        }

        this.releaseDepositQrCode()
        let qrCodeLoaded = false
        try {
          const qrResult = await loadRechargeOrderQrCode()
          const qrBlob = typeof Blob !== 'undefined' && qrResult instanceof Blob
            ? qrResult
            : (qrResult && typeof Blob !== 'undefined' && qrResult.data instanceof Blob
              ? qrResult.data
              : null)
          if (qrBlob) {
            this.depositQrCodeUrl = window.URL.createObjectURL(qrBlob)
            qrCodeLoaded = true
          }
        } catch {
          // 充值订单已经创建成功，二维码失败时保留地址复制流程。
        }

        await this.refreshRechargeRecords()
        this.showToast(
          this.$t(qrCodeLoaded
            ? '充值订单已创建，请按订单金额完成转账'
            : '充值订单已创建，二维码加载失败，请复制地址完成充值'),
          qrCodeLoaded ? 'success' : 'error'
        )
      } catch (error) {
        this.showToast(error.message || this.$t('充值订单创建失败，请稍后重试'), 'error')
      } finally {
        this.depositSubmitting = false
      }
    },
    resetDepositFlow() {
      this.depositOrder = null
      this.releaseDepositQrCode()
    },
    releaseDepositQrCode() {
      if (this.depositQrCodeUrl) {
        window.URL.revokeObjectURL(this.depositQrCodeUrl)
        this.depositQrCodeUrl = ''
      }
    },
    ensureSuccess(result, fallback) {
      if (!result || result.success === false) {
        throw new Error((result && result.message) || fallback)
      }
    },
    async handleActionSubmit(payload) {
      this.submitting = true
      try {
        let refreshAssetOnly = false
        let refreshAccountOnly = false
        if (payload.type === 'transfer') {
          const result = await transferBalance(payload.from, payload.to, payload.amount)
          this.ensureSuccess(result, '账户划转失败')
          this.applyTransferToBalances(payload.from, payload.to, payload.amount)
          refreshAssetOnly = true
        } else if (payload.type === 'buy-card') {
          const result = await buyPointCard(payload.cardAmount, payload.giveCard)
          this.ensureSuccess(result, '点卡购买失败')
          refreshAccountOnly = true
        } else if (payload.type === 'withdraw') {
          const sendType = this.userInfo.username ? 1 : 2
          const result = await withdrawBalance(
            payload.address,
            payload.amount,
            payload.verificationCode,
            sendType
          )
          this.ensureSuccess(result, '提现申请失败')
        } else if (payload.type === 'internal-withdraw') {
          const sendType = this.userInfo.username ? 1 : 2
          const result = await internalWithdrawBalance(
            payload.address,
            payload.amount,
            payload.verificationCode,
            sendType
          )
          this.ensureSuccess(result, '内部转账失败')
        }
        this.actionVisible = false
        if (refreshAccountOnly) {
          await this.refreshAccountBalances()
        } else if (refreshAssetOnly) {
          await this.refreshAssetSummary()
        } else {
          await Promise.all([
            this.refreshAssetSummary(),
            this.refreshAssetOrderRecords({ category: 'withdraw', page: 1 })
          ])
        }
        this.showToast(this.$t('操作成功，数据已重新同步'), 'success')
      } catch (error) {
        this.showToast(error.message || this.$t('操作未完成，请稍后重试'), 'error')
      } finally {
        this.submitting = false
      }
    },
    applyTransferToBalances(from, to, amount) {
      const transferAmount = finiteNumber(amount)
      const fund = finiteNumber(this.balances.fund)
      const fuel = finiteNumber(this.balances.fuel)
      if (transferAmount === null || transferAmount <= 0 || fund === null || fuel === null) return

      const next = { ...this.balances, fund, fuel }
      if (String(from) === '1') next.fund = Math.max(0, next.fund - transferAmount)
      if (String(from) === '2') next.fuel = Math.max(0, next.fuel - transferAmount)
      if (String(to) === '1') next.fund += transferAmount
      if (String(to) === '2') next.fuel += transferAmount
      this.balances = next
      this.lastSync = new Date()
    },
    async handleSendCode(payload = {}) {
      const account = this.userInfo.username || this.userInfo.mobile
      if (!account) {
        this.showToast(this.$t('未找到验证码接收账号'), 'error')
        return
      }
      const sendType = this.userInfo.username ? 1 : 2
      const codeType = payload.type === 'internal-withdraw' ? 4 : 3
      try {
        const result = await sendWithdrawCode(account, codeType, sendType)
        this.ensureSuccess(result, '验证码发送失败')
        this.showToast(this.$t('验证码已发送到绑定账号'), 'success')
      } catch (error) {
        this.showToast(error.message || this.$t('验证码发送失败'), 'error')
      }
    },
    async handleOrbitStrategyCreate(payload) {
      if (this.strategySubmitting) return
      this.strategySubmitting = true
      try {
        if (Number(this.userInfo && this.userInfo.is_stop) === 1) {
          throw new Error(this.$t('当前账号已冻结，无法创建策略'))
        }
        if (this.selectedRobotType !== 1 || !this.activeExchangeRobot || Number(this.activeExchangeRobot.bind_api) !== 1) {
          throw new Error(this.$t('请先绑定 OKX 交易所 API'))
        }
        const robotWasPaused = Number(this.activeExchangeRobot && this.activeExchangeRobot.state) === 2
        await createOrbitStrategy(payload)

        this.orbitStrategyCreateVisible = false
        this.strategyListMode = 'current'
        await this.refreshVisibleStrategies()

        let message = this.$t('UPlus-Orbit AI 创建成功')
        if (robotWasPaused) message += this.$t('，请手动启动机器人')
        this.showToast(message, 'success')
      } catch (error) {
        this.showToast(this.orbitErrorText(error, 'robot.orbit_create_failed_retry'), 'error')
      } finally {
        this.strategySubmitting = false
      }
    },
    async handleStrategyCreate(payload) {
      this.strategySubmitting = true
      try {
        const availabilityIssue = strategyAvailabilityIssue(payload.productKey, this.strategyCapacity)
        if (availabilityIssue) throw new Error(availabilityIssue)
        const spaceIssue = strategySpaceAvailabilityIssue(payload.productKey, this.strategyCapacity, payload.amount)
        if (spaceIssue) throw new Error(spaceIssue)
        const capacityBeforeCreate = this.strategyCapacity
        const robotWasPaused = Number(this.activeExchangeRobot && this.activeExchangeRobot.state) === 2
        const result = await createStrategy(payload)
        if (!strategyCreateSucceeded(result, payload.productKey)) {
          throw new Error((result && result.message) || this.$t('策略创建失败'))
        }

        let robotNameWarning = ''
        if (payload.robotName) {
          const nameResult = await saveRobotName(payload.robotName)
          if (!nameResult || nameResult.success === false) {
            robotNameWarning = (nameResult && nameResult.message) || this.$t('机器人名称保存失败')
          }
        }

        this.strategyCreateVisible = false
        this.strategyListMode = 'current'
        const [exchangeSnapshot, capacityResult] = await Promise.all([
          loadExchangeStrategies(this.selectedRobotType),
          loadStrategyCapacity()
        ])
        this.applyStrategyListSnapshot(exchangeSnapshot, this.strategyListMode)
        this.strategyCapacity = strategyCapacityAfterCreate(
          payload.productKey,
          capacityBeforeCreate,
          capacityResult.capacity,
          payload.amount
        )
        this.loadErrors = [
          ...exchangeSnapshot.strategyErrors,
          ...(capacityResult.error ? [capacityResult.error] : [])
        ]
        this.lastSync = new Date()

        const product = getStrategyProduct(payload.productKey)
        let message = this.$t(`${product.name} 创建成功`)
        if (robotWasPaused) message += this.$t('，请手动启动机器人')
        if (robotNameWarning) message += `；${robotNameWarning}`
        this.showToast(message, robotNameWarning ? 'error' : 'success')
      } catch (error) {
        this.showToast(error.message || this.$t('策略创建失败，请稍后重试'), 'error')
      } finally {
        this.strategySubmitting = false
      }
    },
    async handleRobotActionSubmit(payload) {
      if (robotProductType(payload.robot) === 'orbit') return this.handleOrbitActionSubmit(payload)
      if (
        payload.action === 'modify' &&
        robotProductType(payload.robot) === 'hedge' &&
        finiteNumber(payload.form && payload.form.amount) <
          finiteNumber(payload.robot && payload.robot.raw && payload.robot.raw.space)
      ) {
        try {
          await this.$confirm(
            '修改后的投入金额低于当前投入金额，确认继续修改吗？',
            '确认减少投入金额',
            {
              confirmButtonText: '确认修改',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
        } catch (error) {
          return
        }
      }
      this.robotActionSubmitting = true
      try {
        const result = await executeRobotAction(payload)
        this.ensureSuccess(result, '策略操作失败')
        this.robotActionVisible = false
        const isStatusAction = ['pause', 'resume'].includes(payload.action)
        if (isStatusAction) {
          this.applyLocalRobotStatus(
            payload.robot,
            payload.action === 'pause' ? 'paused' : 'running'
          )
        } else {
          await this.refreshVisibleStrategies()
        }
        if (payload.action === 'delete' && this.isStrategyDetail) {
          this.detailRobotCache = null
          sessionStorage.removeItem('uplusStrategyDetail')
          await this.$router.push('/app/robots')
        }
        const actionText = {
          modify: '修改',
          pause: '暂停',
          resume: '恢复',
          delete: '删除'
        }[payload.action] || '操作'
        this.showToast(
          this.$t(isStatusAction
            ? `策略${actionText}成功`
            : `策略${actionText}成功，列表已重新同步`),
          'success'
        )
      } catch (error) {
        this.showToast(error.message || this.$t('策略操作失败，请稍后重试'), 'error')
      } finally {
        this.robotActionSubmitting = false
      }
    },
    applyLocalRobotStatus(target, status) {
      if (!target) return
      const isTarget = robot => (
        robot === target ||
        (
          String(robot && robot.id) === String(target.id) &&
          String(robot && robot.productType) === String(target.productType) &&
          Number(robotTypeValue(robot)) === Number(robotTypeValue(target))
        )
      )
      const updateStatus = robot => isTarget(robot)
        ? { ...robot, status }
        : robot

      this.robots = this.robots.map(updateStatus)
      this.robotActionRobot = updateStatus(this.robotActionRobot)
      this.robotExceptionRobot = updateStatus(this.robotExceptionRobot)

      if (this.detailRobotCache && isTarget(this.detailRobotCache)) {
        this.detailRobotCache = { ...this.detailRobotCache, status }
        sessionStorage.setItem(
          'uplusStrategyDetail',
          JSON.stringify(this.detailRobotCache)
        )
      }
    },
    showToast(message, type = 'info') {
      if (this.toast.timer) clearTimeout(this.toast.timer)
      this.toast = { visible: true, message, type, timer: null }
      this.toast.timer = setTimeout(() => {
        this.toast.visible = false
      }, 2600)
    },
    openLogoutConfirm() {
      this.logoutConfirmVisible = true
    },
    confirmLogout() {
      this.logoutConfirmVisible = false
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      sessionStorage.removeItem('uplusStrategyDetail')
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.user-portal {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  background: var(--up-bg);
}

.user-portal--leaderboard {
  background: #fff;
}

.user-portal--assets {
  background: #fff;
}

.user-portal--bills {
  background: #fff;
}

.user-portal--profile {
  background: #fff;
}

.strategy-agreement-page {
  position: fixed;
  z-index: 3000;
  inset: 0;
  overflow: auto;
  color: #303747;
  background: #fff;
}

.portal-main {
  flex: 1 0 auto;
  padding-top: 28px;
  padding-bottom: 80px;
}

.portal-panel-enter-active,
.portal-panel-leave-active {
  transition: opacity .16s ease, transform .16s ease;
}

.portal-panel-enter {
  opacity: 0;
  transform: translateY(5px);
}

.portal-panel-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

.detail-route-state {
  display: flex;
  min-height: 360px;
  align-items: center;
  justify-content: center;
  padding: 40px;
  flex-direction: column;
  text-align: center;
}

.detail-route-state > i {
  color: #8fb2f5;
  font-size: 32px;
}

.detail-route-state > strong {
  margin-top: 15px;
  color: #1b1f2a;
  font-size: 18px;
}

.detail-route-state > p {
  margin: 8px 0 0;
  color: #8a90a0;
  font-size: 13px;
}

.detail-route-state > button {
  margin-top: 20px;
  padding: 9px 20px;
  border: 1px solid #c9dbfb;
  border-radius: 8px;
  background: #f3f7fe;
  color: #1f6bff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

@media (max-width: 680px) {
  .portal-main {
    padding-top: 18px;
    padding-bottom: 32px;
  }
}
</style>
